<template>
  <ion-app>
    <ion-router-outlet />
    <v-push-notification-prompt />
  </ion-app>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted } from "vue";
import { bootstrap } from "vue-gtag";
import { useI18n } from "vue-i18n";
import { IonApp, IonRouterOutlet, toastController } from "@ionic/vue";
import { storage } from "@/common/services/storage";
import { useUserStore } from "@/common/store/user.store";
import { CURRENT_USER_REFRESH, GA_MEASUREMENT_ID } from "@/common/constants";
import { openModalFn } from "@/common/components/controllers";
import VCookiesManagerModal from "@/common/components/VCookiesManagerModal.vue";
import VPushNotificationPrompt from "@/common/components/VPushNotificationPrompt.vue";
import { initializePushNotifications } from "@/common/services/notifications.service";

let deferredPrompt: any;

export default defineComponent({
  name: "App",

  components: {
    IonApp,
    IonRouterOutlet,
    VPushNotificationPrompt,
  },

  setup() {
    const { t } = useI18n();
    const userStore = useUserStore();
    let cookiesToast: any;

    const presentInstallAppToast = async () => {
      const toast = await toastController.create({
        message: t("message.pwa.installDisclaimer"),
        buttons: [
          {
            text: t("message.pwa.installButton"),
            role: "submit",
            handler: async () => {
              if (deferredPrompt !== null) {
                deferredPrompt.prompt();
                const { outcome } = await deferredPrompt.userChoice;
                if (outcome === "accepted") {
                  deferredPrompt = null;
                }
              }
            },
          },
          {
            text: t("message.pwa.cancelButton"),
            role: "cancel",
            handler: () => {
              deferredPrompt = null;
            },
          },
        ],
      });

      await toast.present();
      await toast.onDidDismiss();
    };

    const openCookiesManager = async () => {
      await cookiesToast.dismiss();
      const modal = await openModalFn({
        component: VCookiesManagerModal,
      })();
      const { role } = await modal.onDidDismiss();
      if (role === "gesture") {
        history.back();
      }
      if (role === "confirm") {
        localStorage.setItem("acceptedCookies", "true");
      }
      return role;
    };

    const presentConsentCookiesToast = async () => {
      cookiesToast = await toastController.create({
        position: "top",
        cssClass: "cookies-toast",
        message: t("message.cookies.toast.disclaimer"),
        translucent: true,
        buttons: [
          {
            text: t("message.cookies.toast.configure"),
            role: "button",
            handler: async () => {
              const role = await openCookiesManager();
              if (role === "cancel") {
                const acceptedCookies = localStorage.getItem("acceptedCookies");
                if (!acceptedCookies) {
                  presentConsentCookiesToast();
                }
              }
            },
          },
          {
            text: t("message.cookies.toast.acceptAll"),
            role: "submit",
            handler: () => {
              if (GA_MEASUREMENT_ID) {
                bootstrap();
              }
              localStorage.setItem("acceptedAnalytics", "true");
              localStorage.setItem("acceptedCookies", "true");
            },
          },
        ],
      });

      await cookiesToast.present();
      await cookiesToast.onDidDismiss();
    };

    const refreshUser = async (reconcilePushOnAppEntry = false) => {
      const token = await storage.get("token");
      if (token) {
        userStore.fetchCurrentUser({ reconcilePushOnAppEntry });
      }
    };

    let intervalId: number;

    onMounted(async () => {
      refreshUser(true);
      if (CURRENT_USER_REFRESH > 0) {
        intervalId = setInterval(refreshUser, CURRENT_USER_REFRESH);
      }
      const acceptedCookies = localStorage.getItem("acceptedCookies");
      if (!acceptedCookies) {
        presentConsentCookiesToast();
      }

      // Re-sincronizar push tras actualización del service worker
      document.addEventListener("swUpdated", async () => {
        const user = userStore.currentUser;
        if (
          user?.id &&
          user.settings?.notifications?.push &&
          "Notification" in window &&
          Notification.permission === "granted"
        ) {
          initializePushNotifications(user.id).catch((error) => {
            console.error("Error re-syncing push after SW update:", error);
          });
        }
      });

      // Registrar service worker personalizado para notificaciones push (si no está ya registrado)
      if ("serviceWorker" in navigator && process.env.NODE_ENV !== "production") {
        // En desarrollo, registramos el service worker personalizado manualmente
        // En producción se registra automáticamente via registerServiceWorker.ts
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          if (!registration || !registration.active) {
            const swUrl = `${process.env.BASE_URL || "/"}service-worker-custom.js`;
            await navigator.serviceWorker.register(swUrl, {
              scope: process.env.BASE_URL || "/",
            });
            console.log("Service Worker personalizado registrado para notificaciones push");
          }
        } catch (error) {
          console.warn("No se pudo registrar el service worker personalizado:", error);
        }
      }
    });

    onUnmounted(() => {
      if (CURRENT_USER_REFRESH > 0) {
        clearInterval(intervalId);
      }
    });

    window.addEventListener("beforeinstallprompt", (e) => {
      deferredPrompt = e;
      presentInstallAppToast();
    });
  },
});
</script>
<style>
.cookies-toast::part(container) {
  display: flex;
  flex-direction: column;
}
</style>
